import React, { useState, useMemo, useEffect } from 'react';
import { useSettings } from '../../context/SettingsContext';
import { usePlayerComparison } from '../../context/PlayerComparisonContext';
import { Trash2 } from 'lucide-react';
import aflPlayerData from '../../assets/afl_player_data_2026.json';
import { deadHeatShare, formatWins, formatPct } from '../../utils/deadHeat';
import ImpliedOddsInfo from '../common/ImpliedOddsInfo';

// Team logos
import AdlLogo from '../../assets/team_logos/adl.svg';
import BlLogo from '../../assets/team_logos/bl.svg';
import CarlLogo from '../../assets/team_logos/carl.svg';
import CollLogo from '../../assets/team_logos/coll.svg';
import EssLogo from '../../assets/team_logos/ess.svg';
import FreLogo from '../../assets/team_logos/fre.svg';
import GeelLogo from '../../assets/team_logos/geel.svg';
import GcLogo from '../../assets/team_logos/gc.svg';
import GwsLogo from '../../assets/team_logos/gws.svg';
import HawLogo from '../../assets/team_logos/haw.svg';
import MelbLogo from '../../assets/team_logos/melb.svg';
import NmLogo from '../../assets/team_logos/nm.svg';
import PortLogo from '../../assets/team_logos/port.svg';
import RichLogo from '../../assets/team_logos/rich.svg';
import StkLogo from '../../assets/team_logos/stk.svg';
import SydLogo from '../../assets/team_logos/syd.svg';
import WceLogo from '../../assets/team_logos/wce.svg';
import WbdLogo from '../../assets/team_logos/wbd.svg';

// Team helper functions
const TEAM_DISPLAY_NAMES = {
  "Adelaide_Crows": "Adelaide",
  "Brisbane_Lions": "Brisbane", 
  "Carlton": "Carlton",
  "Collingwood": "Collingwood",
  "Essendon": "Essendon",
  "Fremantle": "Fremantle",
  "Geelong_Cats": "Geelong",
  "Gold_Coast_Suns": "Gold Coast",
  "GWS_Giants": "GWS",
  "Hawthorn": "Hawthorn",
  "Melbourne": "Melbourne",
  "North_Melbourne": "North Melbourne",
  "Port_Adelaide": "Port Adelaide",
  "Richmond": "Richmond",
  "St_Kilda": "St Kilda",
  "Sydney_Swans": "Sydney",
  "West_Coast_Eagles": "West Coast",
  "Western_Bulldogs": "Western Bulldogs"
};

const getTeamDisplayName = (teamName) => {
  return TEAM_DISPLAY_NAMES[teamName] || teamName;
};

const getTeamLogo = (teamName) => {
  const logos = {
    "Adelaide_Crows": AdlLogo,
    "Brisbane_Lions": BlLogo,
    "Carlton": CarlLogo,
    "Collingwood": CollLogo,
    "Essendon": EssLogo,
    "Fremantle": FreLogo,
    "Geelong_Cats": GeelLogo,
    "Gold_Coast_Suns": GcLogo,
    "GWS_Giants": GwsLogo,
    "Hawthorn": HawLogo,
    "Melbourne": MelbLogo,
    "North_Melbourne": NmLogo,
    "Port_Adelaide": PortLogo,
    "Richmond": RichLogo,
    "St_Kilda": StkLogo,
    "Sydney_Swans": SydLogo,
    "West_Coast_Eagles": WceLogo,
    "Western_Bulldogs": WbdLogo,
  };
  return logos[teamName] || null;
};

// Marks a cell that clears the threshold / wins its dataset. Group B uses sky to stay distinct.
const LeaderCheck = ({ accent = 'emerald' }) => (
  <svg
    className={`w-2.5 h-2.5 mt-0.5 ${accent === 'sky' ? 'text-sky-400' : 'text-emerald-400'}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
  </svg>
);

const ChevronDown = ({ className = 'w-3 h-3' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
  </svg>
);

// Expand/collapse affordance on mobile group and player rows.
const expandButtonClass = (isExpanded, accent = 'emerald') =>
  `w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-md border cursor-pointer transition-all duration-200 ${
    isExpanded
      ? accent === 'sky'
        ? 'rotate-180 bg-sky-500/10 border-sky-500/30 text-sky-300'
        : 'rotate-180 bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
      : 'border-slate-700/60 text-slate-400 hover:text-slate-200 hover:border-slate-600'
  }`;

const CARD_CLASS = 'bg-slate-950/60 rounded-xl border border-slate-800';
const TABLE_HEAD_CLASS = 'bg-slate-800/20 border-b border-slate-800/80';
const TH_CLASS = 'text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]';
const BODY_ROW_CLASS = 'border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors';
const REMOVE_BUTTON_CLASS = 'text-rose-400 hover:text-rose-300 transition-colors p-1 rounded-md hover:bg-rose-500/10';

const BYOTable = ({ voteData, searchTerm, setSearchTerm, selectedBetType, selectedPlayers, setSelectedPlayers, voteThreshold, setVoteThreshold, selectedGroupA, setSelectedGroupA, selectedGroupB, setSelectedGroupB, removePlayerFromGroup, selectedTeam, setSelectedTeam }) => {
  const { getEnabledKeys } = useSettings();
  const { toggleMobileRow, isMobileRowExpanded } = usePlayerComparison();
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(voteThreshold.toString());

  // Sync input value when voteThreshold changes externally
  useEffect(() => {
    setInputValue(voteThreshold.toString());
  }, [voteThreshold]);

  // Enabled dataset keys (count1..count5, betfair, espn, byb)
  const enabledSourceKeys = getEnabledKeys();
  const enabledSourceSet = new Set(enabledSourceKeys);
  
  // Display order for tables (C1..C5 then external)
  const displayOrder = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
  const enabledDisplayKeys = displayOrder.filter((k) => enabledSourceSet.has(k));
  const keyToLabel = { 
    count1: 'BG 1', count2: 'BG 2', count3: 'BG 3', count4: 'BG 4', count5: 'BG 5', 
    betfair: 'BETFAIR', espn: 'ESPN', byb: 'BYB' 
  };

  // Calculate average of enabled datasets for a player row
  const averageEnabledFromRow = (rowObj) => {
    const keys = enabledDisplayKeys;
    const n = keys.length;
    if (!n) return 0;
    let sum = 0;
    for (const k of keys) {
      const v = Number(rowObj?.[k] ?? 0);
      sum += isNaN(v) ? 0 : v;
    }
    return sum / n;
  };

  // Function to get player image based on name
  const getPlayerImage = (playerName) => {
    const player = aflPlayerData.find(p => p.player_name === playerName);
    return player ? player.image_src : null;
  };

  // Filter available players based on search term
  const filteredPlayers = useMemo(() => {
    if (!voteData?.leaderboard || !searchTerm) return [];
    
    const search = searchTerm.toLowerCase();
    return voteData.leaderboard
      .filter(player => {
        const nameMatch = player.name.toLowerCase().includes(search);
        if (selectedBetType === 'players-a-vs-b') {
          // For A vs B, exclude players from both groups
          const notInGroupA = !selectedGroupA.some(selected => selected.name === player.name);
          const notInGroupB = !selectedGroupB.some(selected => selected.name === player.name);
          return nameMatch && notInGroupA && notInGroupB;
        } else {
          // For other bet types, exclude from main selection
          return nameMatch && !selectedPlayers.some(selected => selected.name === player.name);
        }
      })
      .slice(0, 10); // Limit to 10 results for dropdown
  }, [voteData, searchTerm, selectedPlayers, selectedGroupA, selectedGroupB, selectedBetType]);

  // Add player to selection
  const addPlayer = (player) => {
    // This function should not be used for A vs B bet type
    // A vs B uses the addPlayer function from BYOBrownlow.js
    if (selectedBetType !== 'players-a-vs-b') {
      setSelectedPlayers(prev => [...prev, player]);
    }
    setSearchTerm('');
    setShowPlayerDropdown(false);
  };

  // Remove player from selection
  const removePlayer = (playerName) => {
    setSelectedPlayers(prev => prev.filter(p => p.name !== playerName));
  };

  // Get combined totals data (matching PlayerComparisonTable structure but for combined totals)
  const combinedData = useMemo(() => {
    if (selectedBetType === 'players-a-vs-b') {
      // For A vs B, return data for both groups
      if (!voteData?.leaderboard || (selectedGroupA.length === 0 && selectedGroupB.length === 0)) return null;
      
      const calculateGroupData = (players, groupName) => {
        if (players.length === 0) return null;
        
        const totals = {};
        enabledDisplayKeys.forEach(key => {
          totals[key] = 0;
        });

        players.forEach(selectedPlayer => {
          const playerData = voteData.leaderboard.find(p => p.name === selectedPlayer.name);
          if (playerData) {
            enabledDisplayKeys.forEach(key => {
              if (playerData[key] !== undefined) {
                totals[key] += playerData[key];
              }
            });
          }
        });

        const votes = averageEnabledFromRow(totals);
        
        return {
          name: groupName,
          team: `${players.length} players selected`,
          votes,
          ...totals,
          playerPhotos: players.map(p => getPlayerImage(p.name)).filter(Boolean)
        };
      };

      return {
        groupA: calculateGroupData(selectedGroupA, 'Group A'),
        groupB: calculateGroupData(selectedGroupB, 'Group B')
      };
    } else if (selectedBetType === 'players-vs-team') {
      // For Players vs Team, return data for players and team
      if (!voteData?.leaderboard || (selectedPlayers.length === 0 && !selectedTeam)) return null;
      
      const calculatePlayersData = () => {
        if (selectedPlayers.length === 0) return null;
        
        const totals = {};
        enabledDisplayKeys.forEach(key => {
          totals[key] = 0;
        });

        selectedPlayers.forEach(selectedPlayer => {
          const playerData = voteData.leaderboard.find(p => p.name === selectedPlayer.name);
          if (playerData) {
            enabledDisplayKeys.forEach(key => {
              if (playerData[key] !== undefined) {
                totals[key] += playerData[key];
              }
            });
          }
        });

        const votes = averageEnabledFromRow(totals);
        
        return {
          name: 'Players',
          team: `${selectedPlayers.length} players selected`,
          votes,
          ...totals,
          playerPhotos: selectedPlayers.map(p => getPlayerImage(p.name)).filter(Boolean)
        };
      };

      const calculateTeamData = () => {
        if (!selectedTeam) return null;
        
        const totals = {};
        enabledDisplayKeys.forEach(key => {
          totals[key] = 0;
        });

        // Sum all players from the selected team
        voteData.leaderboard.forEach(player => {
          const playerTeam = player.full_team_name || player.team;
          if (playerTeam === selectedTeam) {
            enabledDisplayKeys.forEach(key => {
              if (player[key] !== undefined) {
                totals[key] += player[key];
              }
            });
          }
        });

        const votes = averageEnabledFromRow(totals);
        
        return {
          name: getTeamDisplayName(selectedTeam),
          team: 'Team Total',
          votes,
          ...totals,
          teamLogo: getTeamLogo(selectedTeam)
        };
      };

      return {
        groupA: calculatePlayersData(),
        groupB: calculateTeamData()
      };
    } else {
      // Original logic for other bet types
      if (!voteData?.leaderboard || selectedPlayers.length === 0) return null;
      
      const totals = {};
      
      // Initialize totals for enabled keys
      enabledDisplayKeys.forEach(key => {
        totals[key] = 0;
      });

      // Sum votes for each enabled dataset
      selectedPlayers.forEach(selectedPlayer => {
        const playerData = voteData.leaderboard.find(p => p.name === selectedPlayer.name);
        if (playerData) {
          enabledDisplayKeys.forEach(key => {
            if (playerData[key] !== undefined) {
              totals[key] += playerData[key];
            }
          });
        }
      });

      // Calculate average votes
      const votes = averageEnabledFromRow(totals);
      
      return {
        name: selectedBetType === 'players-combine-x' ? 'Group A' : `Combined (${selectedPlayers.length} players)`,
        team: `${selectedPlayers.length} players selected`,
        votes,
        ...totals,
        playerPhotos: selectedPlayers.map(p => getPlayerImage(p.name)).filter(Boolean)
      };
    }
  }, [selectedPlayers, selectedGroupA, selectedGroupB, selectedTeam, voteData?.leaderboard, enabledDisplayKeys, selectedBetType]);

  // For "Players to combine for X votes" bet type, winning cell is any cell >= vote threshold
  // For "Players A vs B" and "Players vs Team" bet types, winning cell is when group has highest votes (including ties)
  // For "Players to all poll X votes" bet type, winning cell is when individual player >= vote threshold
  const isWinnerForCount = (countKey, group = null, playerData = null) => {
    if (selectedBetType === 'players-combine-x' && combinedData && !combinedData.groupA) {
      const value = Number(combinedData[countKey] ?? 0);
      return value >= voteThreshold;
    } else if (selectedBetType === 'players-all-poll-x' && playerData) {
      const value = Number(playerData[countKey] ?? 0);
      return value >= voteThreshold;
    } else if ((selectedBetType === 'players-a-vs-b' || selectedBetType === 'players-vs-team') && combinedData?.groupA && combinedData?.groupB) {
      const groupAValue = Number(combinedData.groupA[countKey] ?? 0);
      const groupBValue = Number(combinedData.groupB[countKey] ?? 0);
      const maxValue = Math.max(groupAValue, groupBValue);
      
      // Only consider it a win if there's actual data (not both 0)
      if (maxValue === 0) return false;
      
      if (group === 'A') {
        return groupAValue >= maxValue;
      } else if (group === 'B') {
        return groupBValue >= maxValue;
      }
    }
    return false;
  };

  // Dead-heat win share for the head-to-head bet types: 1 if the group wins
  // the count outright, 0.5 if both groups tie, 0 otherwise (or no data).
  const groupWinShare = (countKey, group) => {
    if (!combinedData?.groupA || !combinedData?.groupB) return 0;
    const a = Number(combinedData.groupA[countKey] ?? 0);
    const b = Number(combinedData.groupB[countKey] ?? 0);
    return deadHeatShare(group === 'A' ? a : b, [a, b]);
  };

  // Counter-side price for the A vs B bet types. The bet targets Group A, so
  // Group B's implied odds are rendered in muted slate rather than emerald to
  // read as the opposing price, not a second recommendation. Under dead-heat
  // scoring the two sides always sum to the full count of enabled datasets.
  const renderGroupBOdds = ({ showPct }) => {
    const denom = enabledDisplayKeys.length || 1;
    let wins = 0;
    for (const key of enabledDisplayKeys) wins += groupWinShare(key, 'B');
    const hitPct = (wins / denom) * 100;
    if (hitPct === 0) {
      return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
    }
    const odds = (1 / (hitPct / 100)).toFixed(2);
    return (
      <div className={`flex flex-col items-center justify-center ${showPct ? 'gap-1' : ''}`} title="Opposite side of the bet">
        <span className={`text-slate-400 font-mono font-semibold tabular-nums ${showPct ? 'text-[13px]' : 'text-xs'}`}>{odds}</span>
        <span className="text-slate-600 text-[10px] font-mono tabular-nums">
          {formatWins(wins)}/{denom}{showPct ? ` (${formatPct(hitPct)}%)` : ''}
        </span>
      </div>
    );
  };

  if (!voteData) {
    return (
      <div className={`${CARD_CLASS} p-4 text-center text-slate-400 text-sm`}>
        <p>Loading vote data...</p>
      </div>
    );
  }

  return (
    <>
      {/* Selected Players Pills - Desktop Only */}
      {selectedPlayers.length > 0 && (
        <div className="hidden md:flex flex-wrap gap-2 mb-2.5">
          {selectedPlayers.map((player) => {
            const playerImageSrc = getPlayerImage(player.name);
            return (
              <div
                key={player.name}
                className="flex items-center space-x-2 bg-slate-900/60 rounded-full pl-1.5 pr-2.5 py-1 border border-slate-700/60"
              >
                {playerImageSrc && (
                  <img 
                    src={playerImageSrc} 
                    alt={player.name}
                    className="w-5 h-5 rounded-full object-cover ring-1 ring-slate-700/60"
                  />
                )}
                <span className="text-slate-200 text-[11px] font-medium">{player.name}</span>
                <button
                  onClick={() => removePlayer(player.name)}
                  className="text-rose-400 hover:text-rose-300 transition-colors ml-1"
                  title={`Remove ${player.name}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          
          {/* Team pill inline with player pills for players-vs-team bet type */}
          {selectedBetType === 'players-vs-team' && selectedTeam && (
            <div className="flex items-center space-x-2 bg-slate-900/60 rounded-full pl-1.5 pr-2.5 py-1 border border-sky-500/40">
              {getTeamLogo(selectedTeam) && (
                <img 
                  src={getTeamLogo(selectedTeam)} 
                  alt={getTeamDisplayName(selectedTeam)}
                  className="w-5 h-5 object-contain"
                />
              )}
              <span className="text-slate-200 text-[11px] font-medium">{getTeamDisplayName(selectedTeam)}</span>
              <button
                onClick={() => setSelectedTeam(null)}
                className="text-rose-400 hover:text-rose-300 transition-colors ml-1"
                title={`Remove ${getTeamDisplayName(selectedTeam)}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Team Pills (Desktop only) - for when no players selected but team is selected */}
      {selectedBetType === 'players-vs-team' && selectedTeam && selectedPlayers.length === 0 && (
        <div className="hidden md:flex flex-wrap gap-2 mb-2.5">
          <div className="flex items-center space-x-2 bg-slate-900/60 rounded-full pl-1.5 pr-2.5 py-1 border border-sky-500/40">
            {getTeamLogo(selectedTeam) && (
              <img 
                src={getTeamLogo(selectedTeam)} 
                alt={getTeamDisplayName(selectedTeam)}
                className="w-5 h-5 object-contain"
              />
            )}
            <span className="text-slate-200 text-[11px] font-medium">{getTeamDisplayName(selectedTeam)}</span>
            <button
              onClick={() => setSelectedTeam(null)}
              className="text-rose-400 hover:text-rose-300 transition-colors ml-1"
              title={`Remove ${getTeamDisplayName(selectedTeam)}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Vote Threshold Input (for players-combine-x and players-all-poll-x bet types) */}
      {(selectedBetType === 'players-combine-x' || selectedBetType === 'players-all-poll-x') && (
        <div className="flex items-center space-x-3 mb-2">
          <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap">
            Vote Threshold
          </label>
          <input
            type="number"
            value={inputValue}
            onChange={(e) => {
              const value = e.target.value;
              setInputValue(value);
              
              // Update the actual threshold only for valid whole numbers
              if (value !== '' && value !== '-') {
                const numValue = parseInt(value, 10);
                if (!isNaN(numValue) && numValue >= 0) {
                  setVoteThreshold(numValue);
                }
              }
            }}
            onBlur={(e) => {
              const value = e.target.value;
              if (value === '' || isNaN(parseInt(value, 10)) || parseInt(value, 10) < 0) {
                setVoteThreshold(0);
                setInputValue('0');
              } else {
                const numValue = parseInt(value, 10);
                setVoteThreshold(numValue);
                setInputValue(numValue.toString());
              }
            }}
            className="bg-slate-950/60 border border-slate-700/60 rounded-md px-2 py-1.5 text-white font-mono tabular-nums w-20 text-xs focus:outline-none focus:border-emerald-500/60 transition-colors"
            step="1"
            min="0"
          />
        </div>
      )}

      {/* Mobile: simplified table (PLAYER, VOTES) with expandable rows */}
      <div className={`md:hidden ${CARD_CLASS} overflow-hidden min-h-28 min-w-0 ${
        selectedBetType === 'players-all-poll-x' ? 'max-h-96' : ''
      }`}>
        <div className={`min-w-0 ${
          selectedBetType === 'players-all-poll-x' ? 'overflow-y-auto' : 'overflow-hidden'
        }`}>
          <table className={`w-full table-fixed ${
            selectedBetType === 'players-all-poll-x' ? '' : 'h-full'
          }`}>
            <colgroup>
              <col className="w-[50%]" />
              <col className="w-[22%]" />
              <col className="w-[15%]" />
              <col className="w-[13%]" />
            </colgroup>
            <thead>
              <tr className={TABLE_HEAD_CLASS}>
                <th className={`text-left px-3 py-2.5 ${TH_CLASS}`}>
                  {(selectedBetType === 'players-a-vs-b' || selectedBetType === 'players-vs-team') ? 'Group' : 'Player'}
                </th>
                <th className="text-center px-2 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] bg-emerald-500/10">
                  Votes
                </th>
                <th className={`text-center px-2 py-2.5 ${TH_CLASS}`}>
                  <span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span>
                </th>
                <th className={`text-center px-2 py-2.5 ${TH_CLASS}`}>
                  
                </th>
              </tr>
            </thead>
            <tbody>
              {selectedBetType === 'players-all-poll-x' ? (
                // All poll X mobile view - individual player rows
                <>
                  {selectedPlayers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center">
                        <p className="text-slate-500 text-xs font-mono">Add players to begin analysis</p>
                      </td>
                    </tr>
                  ) : (
                    selectedPlayers.map((player) => {
                      const playerData = voteData.leaderboard.find(p => p.name === player.name);
                      if (!playerData) return null;
                      
                      const playerImageSrc = getPlayerImage(player.name);
                      const playerAverage = averageEnabledFromRow(playerData);
                      
                      // Calculate how many counts this player meets the threshold
                      let playerHits = 0;
                      const denom = enabledDisplayKeys.length || 1;
                      
                      for (const key of enabledDisplayKeys) {
                        if (isWinnerForCount(key, null, playerData)) {
                          playerHits += 1;
                        }
                      }
                      
                      const hitPct = (playerHits / denom) * 100;
                      const meetsThreshold = playerAverage >= voteThreshold;

                      return (
                        <>
                          <tr key={player.name} className={BODY_ROW_CLASS}>
                            <td className="px-3 py-4">
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {playerImageSrc ? (
                                    <img 
                                      src={playerImageSrc} 
                                      alt={player.name}
                                      className="w-8 h-8 rounded-full object-cover ring-1 ring-slate-700/60"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                      <span className="text-white font-bold text-xs">{player.name.charAt(0)}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-white font-semibold truncate text-xs">
                                    {player.name}
                                  </div>
                                  <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">{playerData.team}</div>
                                </div>
                                <button
                                  className={expandButtonClass(isMobileRowExpanded('byo-all-poll', player.name))}
                                  aria-label={isMobileRowExpanded('byo-all-poll', player.name) ? 'Collapse row' : 'Expand row'}
                                  onClick={() => toggleMobileRow('byo-all-poll', player.name)}
                                >
                                  <ChevronDown />
                                </button>
                              </div>
                            </td>
                            <td className="px-2 py-4 text-center">
                              <span className={`inline-block px-2 py-1 rounded-md border font-mono font-bold tabular-nums text-xs ${
                                meetsThreshold 
                                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' 
                                  : 'border-slate-700/60 bg-slate-800/40 text-slate-400'
                              }`}>
                                {playerAverage.toFixed(2)}
                              </span>
                            </td>
                            <td className="px-2 py-4 text-center">
                              {/* No implied odds for players-all-poll-x - shown in summary table instead */}
                              <span className="text-slate-600 text-[11px] font-mono">-</span>
                            </td>
                            <td className="px-2 py-4 text-center">
                              <button
                                onClick={() => removePlayer(player.name)}
                                className={REMOVE_BUTTON_CLASS}
                                title={`Remove ${player.name}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>

                          {/* Player Expanded details row */}
                          {isMobileRowExpanded('byo-all-poll', player.name) && (
                            <tr className="bg-[#060b16] border-b border-slate-800/50">
                              <td colSpan={4} className="px-0 pb-0">
                                <div className="overflow-x-auto">
                                  <table className="min-w-[600px] w-full">
                                    <thead>
                                      <tr className="bg-slate-800/20">
                                        {/* Count headers (enabled only) */}
                                        {enabledDisplayKeys.map((key) => (
                                          <th key={`h-${key}`} className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">
                                            {keyToLabel[key]}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        {/* Count values */}
                                        {enabledDisplayKeys.map((key) => {
                                          const isWinner = isWinnerForCount(key, null, playerData);
                                          return (
                                            <td key={`v-${key}`} className={`px-2 py-3 text-center ${isWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                              <div className="flex flex-col items-center">
                                                <span className="text-slate-400 font-mono tabular-nums text-xs">
                                                  {typeof playerData[key] === 'number' ? playerData[key].toFixed(2) : (playerData[key] || '0.00')}
                                                </span>
                                                {isWinner && (
                                                  <LeaderCheck />
                                                )}
                                              </div>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })
                  )}
                </>
              ) : (selectedBetType === 'players-a-vs-b' || selectedBetType === 'players-vs-team') ? (
                // A vs B and vs Team mobile view
                <>
                  {!combinedData?.groupA && !combinedData?.groupB ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center">
                        <p className="text-slate-500 text-xs font-mono">
                          {selectedBetType === 'players-vs-team' 
                            ? 'Add players and select a team to begin analysis'
                            : 'Add players to both groups to begin analysis'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {/* Group A Row */}
                      {combinedData?.groupA && (
                        <>
                          <tr className={BODY_ROW_CLASS}>
                            <td className="px-3 py-4">
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {combinedData.groupA.playerPhotos.length > 0 ? (
                                    <>
                                      <img 
                                        src={combinedData.groupA.playerPhotos[0]} 
                                        alt="Group A Player 1"
                                        className="w-8 h-8 rounded-full object-cover ring-2 ring-emerald-500/60"
                                      />
                                      {selectedGroupA.length > 1 && (
                                        <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center ring-2 ring-[#0a1120]">
                                          <span className="text-white font-bold text-xs">+{selectedGroupA.length - 1}</span>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                      <span className="text-white font-bold text-xs">{selectedGroupA.length}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-white font-semibold truncate text-xs">
                                    {selectedBetType === 'players-vs-team' ? 'Players' : 'Group A'}
                                  </div>
                                  <div className="text-[10px] font-mono text-slate-500 truncate">{combinedData.groupA.team}</div>
                                </div>
                                <button
                                  className={expandButtonClass(isMobileRowExpanded('byo-a-vs-b', 'groupA'))}
                                  aria-label={isMobileRowExpanded('byo-a-vs-b', 'groupA') ? 'Collapse row' : 'Expand row'}
                                  onClick={() => toggleMobileRow('byo-a-vs-b', 'groupA')}
                                >
                                  <ChevronDown />
                                </button>
                              </div>
                            </td>
                            <td className="px-2 py-4 text-center">
                              <span className="inline-block px-2 py-1 rounded-md border font-mono font-bold tabular-nums text-xs border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                                {combinedData.groupA.votes.toFixed(2)}
                              </span>
                            </td>
                            <td className="px-2 py-4 text-center">
                              {(() => {
                                if (!combinedData?.groupB) return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                                
                                // Effective counts Group A wins (a tie counts 0.5)
                                let groupAWins = 0;
                                const denom = enabledDisplayKeys.length || 1;

                                for (const key of enabledDisplayKeys) {
                                  groupAWins += groupWinShare(key, 'A');
                                }

                                const hitPct = (groupAWins / denom) * 100;
                                if (hitPct === 0) {
                                  return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                                }

                                const odds = (1 / (hitPct / 100)).toFixed(2);
                                return (
                                  <div className="flex flex-col items-center justify-center">
                                    <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs">{odds}</span>
                                    <span className="text-slate-500 text-[10px] font-mono tabular-nums">{formatWins(groupAWins)}/{denom}</span>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-2 py-4 text-center">
                              <button
                                onClick={() => setSelectedGroupA([])}
                                className={REMOVE_BUTTON_CLASS}
                                title="Clear Group A"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>

                          {/* Group A Expanded details row */}
                          {isMobileRowExpanded('byo-a-vs-b', 'groupA') && (
                            <tr className="bg-[#060b16] border-b border-slate-800/50">
                              <td colSpan={4} className="px-0 pb-0">
                                <div className="overflow-x-auto">
                                  <table className="min-w-[600px] w-full">
                                    <thead>
                                      <tr className="bg-slate-800/20">
                                        {/* Count headers (enabled only) */}
                                        {enabledDisplayKeys.map((key) => (
                                          <th key={`h-${key}`} className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">
                                            {keyToLabel[key]}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        {/* Count values */}
                                        {enabledDisplayKeys.map((key) => {
                                          const isWinner = isWinnerForCount(key, 'A');
                                          return (
                                            <td key={`v-${key}`} className={`px-2 py-3 text-center ${isWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                              <div className="flex flex-col items-center">
                                                <span className="text-slate-400 font-mono tabular-nums text-xs">
                                                  {typeof combinedData.groupA[key] === 'number' ? combinedData.groupA[key].toFixed(2) : (combinedData.groupA[key] || '0.00')}
                                                </span>
                                                {isWinner && (
                                                  <LeaderCheck />
                                                )}
                                              </div>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                      
                      {/* Group B Row */}
                      {combinedData?.groupB && (
                        <>
                          <tr className={BODY_ROW_CLASS}>
                            <td className="px-3 py-4">
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {selectedBetType === 'players-vs-team' && combinedData.groupB.teamLogo ? (
                                    <img 
                                      src={combinedData.groupB.teamLogo} 
                                      alt={combinedData.groupB.name}
                                      className="w-8 h-8 object-contain"
                                    />
                                  ) : combinedData.groupB.playerPhotos && combinedData.groupB.playerPhotos.length > 0 ? (
                                    <>
                                      <img 
                                        src={combinedData.groupB.playerPhotos[0]} 
                                        alt="Group B Player 1"
                                        className="w-8 h-8 rounded-full object-cover ring-2 ring-sky-500/60"
                                      />
                                      {selectedGroupB.length > 1 && (
                                        <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center ring-2 ring-[#0a1120]">
                                          <span className="text-white font-bold text-xs">+{selectedGroupB.length - 1}</span>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                      <span className="text-white font-bold text-xs">{selectedGroupB.length}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-white font-semibold truncate text-xs">
                                    {selectedBetType === 'players-vs-team' ? combinedData.groupB.name : 'Group B'}
                                  </div>
                                  <div className="text-[10px] font-mono text-slate-500 truncate">{combinedData.groupB.team}</div>
                                </div>
                                <button
                                  className={expandButtonClass(isMobileRowExpanded('byo-a-vs-b', 'groupB'), 'sky')}
                                  aria-label={isMobileRowExpanded('byo-a-vs-b', 'groupB') ? 'Collapse row' : 'Expand row'}
                                  onClick={() => toggleMobileRow('byo-a-vs-b', 'groupB')}
                                >
                                  <ChevronDown />
                                </button>
                              </div>
                            </td>
                            <td className="px-2 py-4 text-center">
                              <span className="inline-block px-2 py-1 rounded-md border font-mono font-bold tabular-nums text-xs border-sky-500/30 bg-sky-500/10 text-sky-300">
                                {combinedData.groupB.votes.toFixed(2)}
                              </span>
                            </td>
                            <td className="px-2 py-4 text-center">
                              {/* Group B: counter-side price, de-emphasised */}
                              {renderGroupBOdds({ showPct: false })}
                            </td>
                          <td className="px-2 py-4 text-center">
                            <button
                              onClick={() => selectedBetType === 'players-vs-team' ? setSelectedTeam(null) : setSelectedGroupB([])}
                              className={REMOVE_BUTTON_CLASS}
                              title={selectedBetType === 'players-vs-team' ? 'Clear Team' : 'Clear Group B'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                          </tr>

                          {/* Group B Expanded details row */}
                          {isMobileRowExpanded('byo-a-vs-b', 'groupB') && (
                            <tr className="bg-[#060b16] border-b border-slate-800/50">
                              <td colSpan={4} className="px-0 pb-0">
                                <div className="overflow-x-auto">
                                  <table className="min-w-[600px] w-full">
                                    <thead>
                                      <tr className="bg-slate-800/20">
                                        {/* Count headers (enabled only) */}
                                        {enabledDisplayKeys.map((key) => (
                                          <th key={`h-${key}`} className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">
                                            {keyToLabel[key]}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        {/* Count values */}
                                        {enabledDisplayKeys.map((key) => {
                                          const isWinner = isWinnerForCount(key, 'B');
                                          return (
                                            <td key={`v-${key}`} className={`px-2 py-3 text-center ${isWinner ? 'bg-sky-500/10 ring-1 ring-inset ring-sky-500/30 rounded-md' : ''}`}>
                                              <div className="flex flex-col items-center">
                                                <span className="text-slate-400 font-mono tabular-nums text-xs">
                                                  {typeof combinedData.groupB[key] === 'number' ? combinedData.groupB[key].toFixed(2) : (combinedData.groupB[key] || '0.00')}
                                                </span>
                                                {isWinner && (
                                                  <LeaderCheck accent="sky" />
                                                )}
                                              </div>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              ) : (
                // Original combine-x mobile view
                <>
                  {!combinedData ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center">
                        <p className="text-slate-500 text-xs font-mono">Search and add players to begin analysis</p>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {/* Main row */}
                      <tr className={BODY_ROW_CLASS}>
                        <td className="px-3 py-4">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {combinedData.playerPhotos && combinedData.playerPhotos.length > 0 ? (
                                <>
                                  <img 
                                    src={combinedData.playerPhotos[0]} 
                                    alt="Player 1"
                                    className="w-8 h-8 rounded-full object-cover ring-2 ring-emerald-500/60"
                                  />
                                  {selectedPlayers.length > 1 && (
                                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center ring-2 ring-[#0a1120]">
                                      <span className="text-white font-bold text-xs">+{selectedPlayers.length - 1}</span>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                  <span className="text-white font-bold text-xs">{selectedPlayers.length}</span>
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-white font-semibold truncate text-xs">
                                {combinedData.name}
                              </div>
                              <div className="text-[10px] font-mono text-slate-500 truncate">{combinedData.team}</div>
                            </div>
                            <button
                              className={expandButtonClass(isMobileRowExpanded('byo-combine', 'combined'))}
                              aria-label={isMobileRowExpanded('byo-combine', 'combined') ? 'Collapse row' : 'Expand row'}
                              onClick={() => toggleMobileRow('byo-combine', 'combined')}
                            >
                              <ChevronDown />
                            </button>
                          </div>
                        </td>
                        <td className="px-2 py-4 text-center">
                          <span className={`inline-block px-2 py-1 rounded-md border font-mono font-bold tabular-nums text-xs ${
                            combinedData.votes >= voteThreshold 
                              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' 
                              : 'border-slate-700/60 bg-slate-800/40 text-slate-400'
                          }`}>
                            {combinedData.votes.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-2 py-4 text-center">
                          {(() => {
                            if (selectedBetType !== 'players-combine-x') {
                              return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                            }
                            
                            // Calculate how many counts meet the threshold
                            let leaderHits = 0;
                            const denom = enabledDisplayKeys.length || 1;
                            
                            for (const key of enabledDisplayKeys) {
                              if (isWinnerForCount(key)) {
                                leaderHits += 1;
                              }
                            }
                            
                            const hitPct = (leaderHits / denom) * 100;
                            if (hitPct === 0) {
                              return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                            }
                            
                            const odds = (1 / (hitPct / 100)).toFixed(2);
                            return (
                              <div className="flex flex-col items-center justify-center">
                                <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs">{odds}</span>
                                <span className="text-slate-500 text-[10px] font-mono tabular-nums">{leaderHits}/{denom}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-2 py-4 text-center">
                          <button
                            onClick={() => setSelectedPlayers([])}
                            className={REMOVE_BUTTON_CLASS}
                            title="Clear all players"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>

                      {/* Expanded details row */}
                      {isMobileRowExpanded('byo-combine', 'combined') && (
                        <tr className="bg-[#060b16] border-b border-slate-800/50">
                          <td colSpan={4} className="px-0 pb-0">
                            <div className="overflow-x-auto">
                              <table className="min-w-[600px] w-full">
                                <thead>
                                  <tr className="bg-slate-800/20">
                                    {/* Count headers (enabled only) */}
                                    {enabledDisplayKeys.map((key) => (
                                      <th key={`h-${key}`} className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">
                                        {keyToLabel[key]}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    {/* Count values */}
                                    {enabledDisplayKeys.map((key) => {
                                      const isWinner = isWinnerForCount(key);
                                      return (
                                        <td key={`v-${key}`} className={`px-2 py-3 text-center ${isWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                          <div className="flex flex-col items-center">
                                            <span className="text-slate-400 font-mono tabular-nums text-xs">
                                              {typeof combinedData[key] === 'number' ? combinedData[key].toFixed(2) : (combinedData[key] || '0.00')}
                                            </span>
                                            {isWinner && (
                                              <LeaderCheck />
                                            )}
                                          </div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Desktop: full table */}
      <div className={`hidden md:block ${CARD_CLASS} overflow-hidden min-h-32 min-w-0 ${
        selectedBetType === 'players-all-poll-x' ? 'max-h-96' : ''
      }`}>
        <div className={`overflow-x-auto min-w-0 ${
          selectedBetType === 'players-all-poll-x' ? 'overflow-y-auto' : 'h-full'
        }`}>
          <table className={`w-full min-w-[600px] ${
            selectedBetType === 'players-all-poll-x' ? '' : 'h-full'
          }`}>
            <thead>
              <tr className={TABLE_HEAD_CLASS}>
                <th className={`text-left px-3 py-2.5 ${TH_CLASS} min-w-[180px]`}>
                  {(selectedBetType === 'players-a-vs-b' || selectedBetType === 'players-vs-team') ? 'Group' : 'Player'}
                </th>
                {enabledDisplayKeys.map((key) => (
                  <th key={key} className={`text-center px-2 py-2.5 ${TH_CLASS}`}>
                    {keyToLabel[key]}
                  </th>
                ))}
                <th className="text-center px-3 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] bg-emerald-500/10">
                  Votes
                </th>
                <th className={`text-center px-2 py-2.5 ${TH_CLASS} w-20`}>
                  <span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span>
                </th>
                <th className={`text-center px-3 py-2.5 ${TH_CLASS} w-16`}>
                  
                </th>
              </tr>
            </thead>
            <tbody>
              {selectedBetType === 'players-all-poll-x' ? (
                // All poll X desktop view - individual player rows
                <>
                  {selectedPlayers.length === 0 ? (
                    <tr>
                      <td colSpan={enabledDisplayKeys.length + 4} className="px-3 py-8 text-center">
                        <p className="text-slate-500 text-xs font-mono">Add players to begin analysis</p>
                      </td>
                    </tr>
                  ) : (
                    selectedPlayers.map((player) => {
                      const playerData = voteData.leaderboard.find(p => p.name === player.name);
                      if (!playerData) return null;
                      
                      const playerImageSrc = getPlayerImage(player.name);
                      const playerAverage = averageEnabledFromRow(playerData);
                      
                      // Calculate how many counts this player meets the threshold
                      let playerHits = 0;
                      const denom = enabledDisplayKeys.length || 1;
                      
                      for (const key of enabledDisplayKeys) {
                        if (isWinnerForCount(key, null, playerData)) {
                          playerHits += 1;
                        }
                      }
                      
                      const hitPct = (playerHits / denom) * 100;
                      const meetsThreshold = playerAverage >= voteThreshold;

                      return (
                        <tr key={player.name} className={BODY_ROW_CLASS}>
                          <td className="px-3 py-4 min-w-[180px]">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center space-x-2 flex-shrink-0">
                                {playerImageSrc ? (
                                  <img 
                                    src={playerImageSrc} 
                                    alt={player.name}
                                    className="w-9 h-9 rounded-full object-cover ring-1 ring-slate-700/60"
                                  />
                                ) : (
                                  <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                    <span className="text-white font-bold text-xs">{player.name.charAt(0)}</span>
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-white font-semibold truncate text-[13px]">{player.name}</div>
                                <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{playerData.team}</div>
                              </div>
                            </div>
                          </td>
                          {enabledDisplayKeys.map((key) => {
                            const value = Number(playerData[key] ?? 0);
                            const isWinner = isWinnerForCount(key, null, playerData);
                            return (
                              <td key={key} className={`px-2 py-4 text-center ${isWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                <div className="flex flex-col items-center">
                                  <span className="text-slate-400 font-mono tabular-nums text-[13px]">{value.toFixed(2)}</span>
                                  {isWinner && <LeaderCheck />}
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-3 py-4 text-center">
                            <span className={`inline-block px-2.5 py-1 rounded-md border font-mono font-bold tabular-nums text-[13px] ${
                              meetsThreshold 
                                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' 
                                : 'border-slate-700/60 bg-slate-800/40 text-slate-400'
                            }`}>
                              {playerAverage.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-2 py-4 text-center w-20">
                            {/* No implied odds for players-all-poll-x - shown in summary table instead */}
                            <span className="text-slate-600 text-[11px] font-mono">-</span>
                          </td>
                          <td className="px-3 py-4 text-center">
                            <button
                              onClick={() => removePlayer(player.name)}
                              className={REMOVE_BUTTON_CLASS}
                              title={`Remove ${player.name}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </>
              ) : (selectedBetType === 'players-a-vs-b' || selectedBetType === 'players-vs-team') ? (
                // A vs B and vs Team desktop view
                <>
                  {!combinedData?.groupA && !combinedData?.groupB ? (
                    <tr>
                      <td colSpan={enabledDisplayKeys.length + 4} className="px-3 py-8 text-center">
                        <p className="text-slate-500 text-xs font-mono">
                          {selectedBetType === 'players-vs-team' 
                            ? 'Add players and select a team to begin analysis'
                            : 'Add players to both groups to begin analysis'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {/* Group A Row */}
                      {combinedData?.groupA && (
                        <tr className={BODY_ROW_CLASS}>
                          <td className="px-3 py-4 min-w-[180px]">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center -space-x-2 flex-shrink-0">
                                {combinedData.groupA.playerPhotos.slice(0, 4).map((photoSrc, index) => (
                                  <img 
                                    key={index}
                                    src={photoSrc} 
                                    alt={`Group A Player ${index + 1}`}
                                    className="w-9 h-9 rounded-full object-cover ring-2 ring-emerald-500/60"
                                    style={{ zIndex: 4 - index }}
                                  />
                                ))}
                                {combinedData.groupA.playerPhotos && combinedData.groupA.playerPhotos.length === 0 && (
                                  <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                    <span className="text-white font-bold text-xs">{selectedGroupA.length}</span>
                                  </div>
                                )}
                                {combinedData.groupA.playerPhotos && combinedData.groupA.playerPhotos.length > 4 && (
                                  <div className="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center ring-2 ring-[#0a1120]" style={{ zIndex: 0 }}>
                                    <span className="text-white font-bold text-sm">+{combinedData.groupA.playerPhotos.length - 4}</span>
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-white font-semibold truncate text-[13px]">{selectedBetType === 'players-vs-team' ? 'Players' : 'Group A'}</div>
                                <div className="text-[10px] font-mono text-slate-500 truncate">{combinedData.groupA.team}</div>
                              </div>
                            </div>
                          </td>
                          {enabledDisplayKeys.map((key) => {
                            const value = Number(combinedData.groupA[key] ?? 0);
                            const isWinner = isWinnerForCount(key, 'A');
                            return (
                              <td key={key} className={`px-2 py-4 text-center ${isWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                                <div className="flex flex-col items-center">
                                  <span className="text-slate-400 font-mono tabular-nums text-[13px]">{value.toFixed(2)}</span>
                                  {isWinner && <LeaderCheck />}
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-3 py-4 text-center">
                            <span className="inline-block px-2.5 py-1 rounded-md border font-mono font-bold tabular-nums text-[13px] border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                              {combinedData.groupA.votes.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-center">
                            {(() => {
                              if (!combinedData?.groupB) return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                              
                              // Effective counts Group A wins (a tie counts 0.5)
                              let groupAWins = 0;
                              const denom = enabledDisplayKeys.length || 1;

                              for (const key of enabledDisplayKeys) {
                                groupAWins += groupWinShare(key, 'A');
                              }

                              const hitPct = (groupAWins / denom) * 100;
                              if (hitPct === 0) {
                                return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                              }

                              const odds = (1 / (hitPct / 100)).toFixed(2);
                              return (
                                <div className="flex flex-col items-center justify-center gap-1">
                                  <span className="text-emerald-300 font-mono font-bold tabular-nums text-[13px]">{odds}</span>
                                  <span className="text-slate-500 text-[10px] font-mono tabular-nums">{formatWins(groupAWins)}/{denom} ({formatPct(hitPct)}%)</span>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-4 text-center">
                            <button
                              onClick={() => setSelectedGroupA([])}
                              className={REMOVE_BUTTON_CLASS}
                              title="Clear Group A"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      )}
                      
                      {/* Group B Row */}
                      {combinedData?.groupB && (
                        <tr className={BODY_ROW_CLASS}>
                          <td className="px-3 py-4 min-w-[180px]">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center -space-x-2 flex-shrink-0">
                                {selectedBetType === 'players-vs-team' && combinedData.groupB.teamLogo ? (
                                  <img 
                                    src={combinedData.groupB.teamLogo} 
                                    alt={combinedData.groupB.name}
                                    className="w-9 h-9 object-contain"
                                  />
                                ) : combinedData.groupB.playerPhotos && combinedData.groupB.playerPhotos.slice(0, 4).map((photoSrc, index) => (
                                  <img 
                                    key={index}
                                    src={photoSrc} 
                                    alt={`Group B Player ${index + 1}`}
                                    className="w-9 h-9 rounded-full object-cover ring-2 ring-sky-500/60"
                                    style={{ zIndex: 4 - index }}
                                  />
                                ))}
                                {!selectedBetType.includes('players-vs-team') && combinedData.groupB.playerPhotos && combinedData.groupB.playerPhotos.length === 0 && (
                                  <div className="w-9 h-9 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                    <span className="text-white font-bold text-xs">{selectedGroupB.length}</span>
                                  </div>
                                )}
                                {!selectedBetType.includes('players-vs-team') && combinedData.groupB.playerPhotos && combinedData.groupB.playerPhotos.length > 4 && (
                                  <div className="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center ring-2 ring-[#0a1120]" style={{ zIndex: 0 }}>
                                    <span className="text-white font-bold text-sm">+{combinedData.groupB.playerPhotos.length - 4}</span>
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-white font-semibold truncate text-[13px]">{selectedBetType === 'players-vs-team' ? combinedData.groupB.name : 'Group B'}</div>
                                <div className="text-[10px] font-mono text-slate-500 truncate">{combinedData.groupB.team}</div>
                              </div>
                            </div>
                          </td>
                          {enabledDisplayKeys.map((key) => {
                            const value = Number(combinedData.groupB[key] ?? 0);
                            const isWinner = isWinnerForCount(key, 'B');
                            return (
                              <td key={key} className={`px-2 py-4 text-center ${isWinner ? 'bg-sky-500/10 ring-1 ring-inset ring-sky-500/30 rounded-md' : ''}`}>
                                <div className="flex flex-col items-center">
                                  <span className="text-slate-400 font-mono tabular-nums text-[13px]">{value.toFixed(2)}</span>
                                  {isWinner && <LeaderCheck accent="sky" />}
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-3 py-4 text-center">
                            <span className="inline-block px-2.5 py-1 rounded-md border font-mono font-bold tabular-nums text-[13px] border-sky-500/30 bg-sky-500/10 text-sky-300">
                              {combinedData.groupB.votes.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-center">
                            {/* Group B: counter-side price, de-emphasised */}
                            {renderGroupBOdds({ showPct: true })}
                          </td>
                          <td className="px-3 py-4 text-center">
                            <button
                              onClick={() => selectedBetType === 'players-vs-team' ? setSelectedTeam(null) : setSelectedGroupB([])}
                              className={REMOVE_BUTTON_CLASS}
                              title={selectedBetType === 'players-vs-team' ? 'Clear Team' : 'Clear Group B'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </>
              ) : (
                // Original combine-x desktop view
                <>
                  {!combinedData ? (
                    <tr>
                      <td colSpan={enabledDisplayKeys.length + 4} className="px-3 py-8 text-center">
                        <p className="text-slate-500 text-xs font-mono">Search and add players to begin analysis</p>
                      </td>
                    </tr>
                  ) : (
                <tr className={BODY_ROW_CLASS}>
                  {/* Combined Player Info */}
                  <td className="px-3 py-4 min-w-[180px]">
                    <div className="flex items-center space-x-3">
                      {/* Group of player photos */}
                      <div className="flex items-center -space-x-2 flex-shrink-0">
                        {combinedData.playerPhotos.slice(0, 4).map((photoSrc, index) => (
                          <img 
                            key={index}
                            src={photoSrc} 
                            alt={`Player ${index + 1}`}
                            className="w-9 h-9 rounded-full object-cover ring-1 ring-slate-700/60"
                            style={{ zIndex: 4 - index }}
                          />
                        ))}
                        {combinedData.playerPhotos && combinedData.playerPhotos.length === 0 && (
                          <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                            <span className="text-white font-bold text-xs">{selectedPlayers.length}</span>
                          </div>
                        )}
                        {combinedData.playerPhotos && combinedData.playerPhotos.length > 4 && (
                          <div className="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center ring-2 ring-[#0a1120]" style={{ zIndex: 0 }}>
                            <span className="text-white font-bold text-sm">+{combinedData.playerPhotos.length - 4}</span>
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-white font-semibold truncate text-[13px]">
                          {combinedData.name}
                        </div>
                        <div className="text-[10px] font-mono text-slate-500 truncate">{combinedData.team}</div>
                      </div>
                    </div>
                  </td>

                  {/* Count columns - show combined totals for enabled datasets */}
                  {enabledDisplayKeys.map((key) => {
                    const isWinner = isWinnerForCount(key);
                    return (
                      <td key={key} className={`px-2 py-4 text-center ${isWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : ''}`}>
                        <div className="flex flex-col items-center">
                          <span className="text-slate-400 font-mono tabular-nums text-[13px]">
                            {typeof combinedData[key] === 'number' ? combinedData[key].toFixed(2) : (combinedData[key] || '0.00')}
                          </span>
                          {isWinner && (
                            <LeaderCheck />
                          )}
                        </div>
                      </td>
                    );
                  })}

                  {/* Votes - dynamic average of enabled datasets */}
                  <td className="px-3 py-4 text-center">
                    <span className={`inline-block px-2 py-1 rounded border font-semibold text-sm ${
                      combinedData.votes >= voteThreshold 
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' 
                        : 'border-slate-700/60 bg-slate-800/40 text-slate-400'
                    }`}>
                      {combinedData.votes.toFixed(2)}
                    </span>
                  </td>

                  {/* Implied Odds - Calculate based on threshold for combine-X bet type */}
                  <td className="px-2 py-4 text-center w-20">
                    {(() => {
                      if (selectedBetType !== 'players-combine-x') {
                        return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                      }
                      
                      // Calculate how many counts meet the threshold
                      let leaderHits = 0;
                      const denom = enabledDisplayKeys.length || 1;
                      
                      for (const key of enabledDisplayKeys) {
                        if (isWinnerForCount(key)) {
                          leaderHits += 1;
                        }
                      }
                      
                      const hitPct = (leaderHits / denom) * 100;
                      if (hitPct === 0) {
                        return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                      }
                      
                      const odds = (1 / (hitPct / 100)).toFixed(2);
                      return (
                        <div className="flex flex-col items-center justify-center gap-1">
                          <span className="text-emerald-300 font-mono font-bold tabular-nums text-[13px]">{odds}</span>
                          <span className="text-slate-500 text-[10px] font-mono tabular-nums">{leaderHits}/{denom} ({formatPct(hitPct)}%)</span>
                        </div>
                      );
                    })()}
                  </td>

                  {/* Clear All Button */}
                  <td className="px-3 py-4 text-center">
                    <button
                      onClick={() => setSelectedPlayers([])}
                      className={REMOVE_BUTTON_CLASS}
                      title="Clear all players"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default BYOTable;
